import Constants from '../../data/constants';
import { formatPriceName } from '../../utils/priceName';

export { formatPriceName };

// Source filter is multi-select — an empty selection means "all sources".
export const SOURCE_OPTIONS = [
    { label: 'Mary', value: 'mary' },
    { label: 'Auditoría', value: 'auditor' },
    { label: 'Competencia', value: 'competencia' }
];

export const SOURCE_LABELS = { mary: 'Mary', auditor: 'Auditoría', competencia: 'Competencia' };
export const SOURCE_COLORS = { mary: 'blue', auditor: 'green', competencia: 'volcano' };
// Design-system tones for the source pills (StatusTag): Mary = brand, auditor = success, competition = warning.
export const SOURCE_TONES = { mary: 'brand', auditor: 'success', competencia: 'warning' };

export const ROLE_OPTIONS = Object.values(Constants.USER_TYPES).map((v) => ({ label: v, value: v }));

export const CHANNEL_OPTIONS = [
    { label: 'Moderno', value: Constants.CHANNEL.MODERNO },
    { label: 'Tradicional', value: Constants.CHANNEL.TRADICIONAL }
];

// Wholesale (mayorista) handling on the charts: include everything, drop wholesale, or isolate it.
export const MAYORISTA_OPTIONS = [
    { label: 'Todos', value: 'all' },
    { label: 'Sin mayoristas', value: 'exclude' },
    { label: 'Solo mayoristas', value: 'only' }
];

// Every categorical filter is multi-select (an empty array means "no filter / all"). The
// non-list controls stay scalar: mayorista mode, the outlier toggle, and the search box.
export const DEFAULT_FILTERS = {
    source: [],
    category: [],
    segment: [],
    clientId: [],
    storeId: [],
    userId: [],
    userType: [],
    channel: [],
    tagId: [],
    grams: [],
    storeType: [],
    mayorista: 'all',
    hideOutliers: false,
    search: ''
};

// Normalize a filter value (array | scalar | null) to a clean array of real values.
const asList = (v) => {
    if (Array.isArray(v)) return v.filter((x) => x != null && x !== '');
    return v != null && v !== '' ? [v] : [];
};

/**
 * Turn the current filters + date range into a plain query object.
 * Multi-selects go over the wire as comma-separated lists (URLSearchParams-safe strings);
 * empty filters are omitted so `new URLSearchParams` never serializes "undefined"/empty keys.
 */
export const filtersToQuery = (filters, dateRange) => {
    const q = {};
    const join = (key, v) => {
        const a = asList(v);
        if (a.length) q[key] = a.join(',');
    };
    join('source', filters.source);
    join('category', filters.category);
    join('segment', filters.segment);
    join('clientId', filters.clientId);
    join('storeId', filters.storeId);
    join('userId', filters.userId);
    join('userType', filters.userType);
    join('channel', filters.channel);
    join('tagId', filters.tagId);
    join('grams', filters.grams);
    join('storeType', filters.storeType);
    // Wholesale mode → the flags the backend understands. 'all' sends neither.
    if (filters.mayorista === 'exclude') q.excludeMayorista = 'true';
    else if (filters.mayorista === 'only') q.onlyMayorista = 'true';
    if (filters.search && filters.search.trim()) q.search = filters.search.trim();
    if (dateRange && dateRange[0] && dateRange[1]) {
        q.startDate = dateRange[0].startOf('day').toISOString();
        q.endDate = dateRange[1].endOf('day').toISOString();
    }
    return q;
};

// True when an axios request was aborted (superseded by a newer one) — not a real error.
export const isCanceled = (e) => e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError';

// Mirrors the service's MAX_EXPORT_ROWS: the export returns at most this many (newest) captures.
export const EXPORT_MAX_ROWS = 50000;
export const fmtCount = (n) => Number(n || 0).toLocaleString('es');

// How many filters differ from their defaults (the date range is always set, so it isn't counted).
export const countActiveFilters = (filters = {}) =>
    Object.keys(DEFAULT_FILTERS).reduce((n, key) => {
        const v = filters[key];
        if (Array.isArray(v)) return n + (v.length ? 1 : 0);
        if (key === 'mayorista') return n + (v && v !== 'all' ? 1 : 0);
        if (key === 'search') return n + (v && String(v).trim() ? 1 : 0);
        return n + (v ? 1 : 0);
    }, 0);
